import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LevelService } from '../../Services/level.service';
import { INSCRIPTIONS_PATH, SUPERIOR_INSCRIPTIONS_LINK } from '../Inscriptions/Inscriptions.component';
import { WorkshopService } from '../../Services/workshop.service';

interface Workshop {
  id: number;
  name: string;
  /** Level name shown as a badge under the title (e.g. "Secundario") */
  levelName: string;
  daysHours: string;
  teacher: string;
  paragraphs: string[];
  images: string[];
  enableRegistration: boolean;
  registrationText: string;
  /** Whether the full profile ({id} endpoint) was already fetched */
  detailLoaded: boolean;
}

interface GalleryPhoto {
  image: string;
  title: string;
}

@Component({
  selector: 'app-level-profile',
  templateUrl: './LevelProfile.component.html',
  styleUrls: ['./LevelProfile.component.scss']
})
export class LevelProfileComponent implements OnInit {
  /** Extra-curricular (optional) workshops for this level (GET /public/workshops) */
  workshops: Workshop[] = [];

  /** Short workshops-schedule blurb (HTML) shown in the sidebar */
  workshopsSchedule = '';

  activeWorkshop: Workshop | null = null;

  /**
   * Active workshop images mapped to the gallery's photo shape.
   * Stored as a stable reference (not a getter) so the gallery's [photos]
   * input doesn't change every change-detection cycle and reset its index.
   */
  activeWorkshopPhotos: { image: string; title: string }[] = [];

  private updateActiveWorkshopPhotos(): void {
    if (!this.activeWorkshop) {
      this.activeWorkshopPhotos = [];
      return;
    }
    const name = this.activeWorkshop.name;
    this.activeWorkshopPhotos = this.activeWorkshop.images.map(image => ({ image, title: name }));
  }

  /** Active tab, mirrors the web `optionsList` pattern */
  activeOption = 'proposal';

  /** Tabs section, used to scroll to the "Propuesta Educativa" tab */
  @ViewChild('proposalSection') proposalSection?: ElementRef<HTMLElement>;

  // ===== Data from GET /public/levels/{id} =====
  level: any = null;
  levelName = '';
  coverTitle = '';
  coverSubtitle = '';
  coverDescription = '';
  coverImage = '';
  /** Cover background color (from the level's `background_color`); null = default */
  coverBackgroundColor: string | null = null;
  galleryPhotos: GalleryPhoto[] = [];
  /** Accordion links shown over the cover (Horarios / Reglamentos / Talleres) */
  coverLinks: { label: string; description: string }[] = [];
  /** Gallery title/subtitle from the level endpoint (with sensible fallbacks) */
  galleryTitle = '';
  gallerySubtitle = '';
  /** Raw HTML of the educational proposal; rendered via the safeHtml pipe */
  proposalHtml = '';
  /** Raw HTML of the level regulations; rendered via the safeHtml pipe */
  regulationsHtml = '';
  contactPhone = '';
  contactEmail = '';
  workshopsTitle = 'Talleres';
  /** True when the level provides its own workshops_title (vs the default) */
  hasCustomWorkshopsTitle = false;

  /** Cover CTA label */
  get enrollLabel(): string {
    return 'Inscripciones';
  }

  // ===== Link de los botones de inscripción (CUA-133) =====
  /**
   * Link al que van los botones de "Inscripciones" de este nivel.
   * Sale de `registration_link` del nivel si el panel lo configuró; si no, se
   * deduce de la modalidad para que Nivel Superior entre directo a su tab.
   */
  enrollLink: string = INSCRIPTIONS_PATH;

  /** Ruta interna del link (null cuando apunta a un sitio externo) */
  enrollPath: string | null = INSCRIPTIONS_PATH;

  /** Query params de la ruta interna, p. ej. `{ tab: 'superior' }` */
  enrollQueryParams: { [key: string]: string } = {};

  /** Loading flags for skeleton placeholders */
  levelLoading = true;
  workshopsLoading = true;
  /** Skeleton rows for the workshops sidebar list */
  workshopSkeletons = [0, 1, 2, 3];

  constructor(
    private route: ActivatedRoute,
    private levelService: LevelService,
    private workshopService: WorkshopService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(async params => {
      const id = params.get('id');
      if (id) {
        await this.loadLevel(id);
        await this.loadWorkshops();
      }
    });
  }

  /** Fetch the level profile and map it onto the page + child blocks */
  private async loadLevel(id: string): Promise<void> {
    try {
      await this.fetchLevel(id);
    } finally {
      this.levelLoading = false;
    }
  }

  private async fetchLevel(id: string): Promise<void> {
    const res = await this.levelService.getLevel(id);
    const data = res?.data;
    if (!data) {
      return;
    }
    this.level = data;
    this.levelName = data.name || '';
    this.coverTitle = data.name ? 'Nivel ' + data.name : '';
    this.coverSubtitle = data.short_description || '';
    this.coverDescription = data.short_description || '';
    this.coverImage = data.main_image || '';
    this.coverBackgroundColor = data.background_color || null;
    this.proposalHtml = data.educational_proposal || '';
    this.regulationsHtml = data.regulations_full_info || '';
    this.contactPhone = data.contact?.phone || '';
    this.contactEmail = data.contact?.email || '';
    this.galleryTitle = data.gallery_title || '';
    this.gallerySubtitle = data.gallery_subtitle || '';
    this.workshopsSchedule = data.workshops_schedule || '';
    const rawWorkshopsTitle = (data.workshops_title || '').trim();
    this.hasCustomWorkshopsTitle = !!rawWorkshopsTitle;
    this.workshopsTitle = rawWorkshopsTitle || 'Talleres';
    this.coverLinks = [
      { label: 'Horarios', description: data.general_schedule || '' },
      { label: 'Reglamento', description: data.regulations_short_info || '' },
      { label: this.workshopsTitle, description: data.workshops_short_info || '' },
    ].filter(link => link.description.trim() !== '');
    this.galleryPhotos = Array.isArray(data.gallery)
      ? data.gallery.map(g => ({ image: g.url, title: g.label || '' }))
      : [];
    this.setEnrollLink(this.resolveEnrollLink(data));
  }

  /**
   * Link de inscripción del nivel. Prioriza el campo configurable del panel
   * (`registration_link`); sin ese dato, Nivel Superior va al tab que le
   * corresponde y el resto a la página de inscripciones.
   */
  private resolveEnrollLink(data: any): string {
    const configured = (data.registration_link || '').trim();
    if (configured) {
      return configured;
    }
    return this.isTertiary(data) ? SUPERIOR_INSCRIPTIONS_LINK : INSCRIPTIONS_PATH;
  }

  /** Modalidad terciaria: primero `kind`, y si no viene se deduce de slug/nombre */
  private isTertiary(data: any): boolean {
    const kind = (data.kind || '').toLowerCase();
    if (kind) {
      return kind === 'tertiary';
    }
    const text = `${data.slug || ''} ${data.name || ''}`.toLowerCase();
    return text.includes('terciari') || text.includes('superior');
  }

  /**
   * Parte el link en ruta + query para poder navegar con routerLink. Un link
   * absoluto del propio sitio también se navega interno; los externos quedan
   * como href.
   */
  private setEnrollLink(link: string): void {
    this.enrollLink = link;
    this.enrollPath = null;
    this.enrollQueryParams = {};

    let relative = link;
    if (/^https?:\/\//i.test(link)) {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      if (!origin || link.toLowerCase().indexOf(origin.toLowerCase()) !== 0) {
        return;
      }
      relative = link.substring(origin.length) || '/';
    }

    const [path, query] = relative.split('?');
    this.enrollPath = path || '/';
    (query || '')
      .split('&')
      .filter(Boolean)
      .forEach(pair => {
        const [key, value] = pair.split('=');
        if (key) {
          this.enrollQueryParams[decodeURIComponent(key)] = decodeURIComponent(value || '');
        }
      });
  }

  /** Fetch the public workshops, keep only the ones for the current level */
  private async loadWorkshops(): Promise<void> {
    try {
      await this.fetchWorkshops();
    } finally {
      this.workshopsLoading = false;
    }
  }

  private async fetchWorkshops(): Promise<void> {
    const res = await this.workshopService.getPublicWorkshops();
    // The payload is wrapped as { data: { data: [...] } }
    const list = Array.isArray(res?.data?.data) ? res.data.data : res?.data;
    if (!Array.isArray(list)) {
      return;
    }

    // Keep only the workshops of the current level (each workshop carries a
    // `level` object with its id). Schedules are derived from this subset.
    const levelId = this.level?.id;
    const forLevel = levelId != null
      ? list.filter(w => this.workshopLevelId(w) === levelId)
      : list;

    this.workshops = forLevel.map(w => this.mapWorkshop(w));

    if (this.workshops.length) {
      this.setActiveWorkshop(this.workshops[0]);
    } else {
      this.activeWorkshop = null;
    }
  }

  /** The API returns `level` as an object ({ id, name }); be defensive about its shape */
  private workshopLevelId(w: any): number | null {
    if (w == null) {
      return null;
    }
    if (typeof w.level === 'object' && w.level) {
      return w.level.id ?? null;
    }
    if (typeof w.level === 'number') {
      return w.level;
    }
    return w.level_id ?? null;
  }

  /** Map a list item to the sidebar/detail view model */
  private mapWorkshop(w: any): Workshop {
    return {
      id: w.id,
      name: w.name || '',
      levelName: w.level?.name || '',
      daysHours: w.days_hours || '',
      teacher: '',
      paragraphs: this.toParagraphs(w.description),
      images: w.main_image?.large ? [w.main_image.large] : [],
      enableRegistration: !!w.enable_registration,
      registrationText: '',
      detailLoaded: false
    };
  }

  /** Split a free-text description into paragraphs on line breaks */
  private toParagraphs(text: string | null | undefined): string[] {
    return text
      ? text.split('\n').map(s => s.trim()).filter(Boolean)
      : [];
  }

  /** Enrich the active workshop with teacher + full gallery from /public/workshops/{id} */
  private async loadWorkshopDetail(workshop: Workshop): Promise<void> {
    if (workshop.detailLoaded) {
      return;
    }
    const res = await this.workshopService.getPublicWorkshop(workshop.id);
    // Unwrap a possible { data: { data: {...} } } envelope
    const data = res?.data?.data ?? res?.data;
    if (!data) {
      return;
    }
    workshop.teacher = data.teacher?.name || '';
    workshop.registrationText = data.registration_text || '';
    workshop.paragraphs = this.toParagraphs(data.description).length
      ? this.toParagraphs(data.description)
      : workshop.paragraphs;

    const images = data.images
      ? Object.entries(data.images)
          .filter(([key]) => key !== 'logo')
          .map(([, img]: [string, any]) => img?.large || img?.medium || img?.small)
          .filter(Boolean)
      : [];
    if (images.length) {
      workshop.images = images as string[];
    }
    workshop.detailLoaded = true;

    // Refresh the gallery photos now that the full image set is available
    if (this.activeWorkshop === workshop) {
      this.updateActiveWorkshopPhotos();
    }
  }

  /** Ordered list of visible tabs; "Reglamentos" only shows if it has content */
  get optionTabs(): string[] {
    const tabs = ['proposal', 'workshops'];
    if (this.regulationsHtml) {
      tabs.push('regulations');
    }
    return tabs;
  }

  setActiveOption(option: string): void {
    this.activeOption = option;
  }

  /** Activate the "Propuesta Educativa" tab and scroll down to it */
  goToProposal(): void {
    this.activeOption = 'proposal';
    setTimeout(() => {
      this.proposalSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  getSlideStyles() {
    const tabs = this.optionTabs;
    const totalTabs = tabs.length;
    const activeIndex = Math.max(0, tabs.indexOf(this.activeOption));
    const widthPercent = 100 / totalTabs;
    const leftPercent = widthPercent * activeIndex;

    return {
      width: `${widthPercent}%`,
      left: `${leftPercent}%`
    };
  }

  setActiveWorkshop(workshop: Workshop): void {
    this.activeWorkshop = workshop;
    this.updateActiveWorkshopPhotos();
    this.loadWorkshopDetail(workshop);
  }

  /** Mobile dropdown: pick the workshop to show by its id */
  onWorkshopSelect(event: Event): void {
    const id = (event.target as HTMLSelectElement).value;
    const workshop = this.workshops.find(w => String(w.id) === id);
    if (workshop) {
      this.setActiveWorkshop(workshop);
    }
  }
}
